import React, { useState } from 'react';
import { formatDateToTime12h } from '../../utils/timeDisplay';
import { motion, AnimatePresence } from 'framer-motion';
import { ChartBarIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const PerformanceBar = ({ isVisible, onClose, data }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="fixed top-4 left-4 right-4 z-50 flex justify-center"
      >
        <div className="bg-black bg-opacity-90 backdrop-blur-md rounded-lg px-4 md:px-6 py-3 text-white shadow-2xl border border-gray-600 max-w-7xl w-full">
          {/* Main Bar - Always Visible */}
          <div className="flex items-center justify-between w-full">
            {/* Performance Icon */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <ChartBarIcon className="w-5 h-5 text-blue-400" />
              <span className="text-sm font-medium text-gray-300 hidden sm:block">Performance</span>
            </div>

            {/* Core Metrics - Always Visible */}
            <div className="flex items-center space-x-3 md:space-x-6 text-sm flex-1 justify-center overflow-hidden">
              <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                <span className="text-green-400">👥</span>
                <span className="text-gray-300 hidden sm:inline">Employees:</span>
                <span className="font-semibold text-green-400">{data.employees || 0}</span>
              </div>
              <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                <span className="text-blue-400">📊</span>
                <span className="text-gray-300 hidden sm:inline">Attendance:</span>
                <span className="font-semibold text-blue-400">{data.attendance || '0%'}</span>
              </div>
              <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                <span className="text-yellow-400">⏳</span>
                <span className="text-gray-300 hidden sm:inline">Pending:</span>
                <span className="font-semibold text-yellow-400">{data.pending || 0}</span>
              </div>
              <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0 hidden md:flex">
                <span className="text-purple-400">🚀</span>
                <span className="text-gray-300">Productivity:</span>
                <span className="font-semibold text-purple-400">{data.productivity || '0%'}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors flex items-center space-x-1"
                title={isExpanded ? 'Collapse' : 'Expand details'}
              >
                {isExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                title="Close performance bar"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Expanded Details */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="border-t border-gray-600 mt-3 pt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 text-sm">
                    {/* Attendance Details */}
                    <div className="space-y-2">
                      <h4 className="text-blue-400 font-medium flex items-center space-x-1">
                        <span>📊</span>
                        <span>Attendance</span>
                      </h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Today:</span>
                          <span className="text-white">{data.attendanceToday || data.attendance || '0%'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">This Week:</span>
                          <span className="text-white">{data.attendanceWeek || '92%'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">This Month:</span>
                          <span className="text-white">{data.attendanceMonth || '89%'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Leave Management */}
                    <div className="space-y-2">
                      <h4 className="text-red-400 font-medium flex items-center space-x-1">
                        <span>🏖️</span>
                        <span>Leaves</span>
                      </h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Pending:</span>
                          <span className="text-white">{data.pending || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Approved:</span>
                          <span className="text-white">{data.leaves || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Remaining:</span>
                          <span className="text-white">{data.remainingLeaves || '15'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Projects & Tasks */}
                    <div className="space-y-2">
                      <h4 className="text-cyan-400 font-medium flex items-center space-x-1">
                        <span>💼</span>
                        <span>Projects</span>
                      </h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Active:</span>
                          <span className="text-white">{data.projects || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Completed:</span>
                          <span className="text-white">{data.completedProjects || '12'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">On Track:</span>
                          <span className="text-white">{data.onTrackProjects || '85%'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="space-y-2">
                      <h4 className="text-purple-400 font-medium flex items-center space-x-1">
                        <span>🚀</span>
                        <span>Performance</span>
                      </h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Efficiency:</span>
                          <span className="text-white">{data.efficiency || '94%'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Quality:</span>
                          <span className="text-white">{data.quality || '96%'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Growth:</span>
                          <span className="text-white">{data.growth || '+12%'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Stats Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-gray-700 text-xs space-y-2 sm:space-y-0">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      <div className="flex items-center space-x-1">
                        <span className="text-orange-400">⏰</span>
                        <span className="text-gray-400">Avg Hours:</span>
                        <span className="text-white">{data.avgHours || '8.2h'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-pink-400">💡</span>
                        <span className="text-gray-400">Innovation:</span>
                        <span className="text-white">{data.innovation || '87%'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-indigo-400">🎯</span>
                        <span className="text-gray-400">Goals Met:</span>
                        <span className="text-white">{data.goalsMet || '9/10'}</span>
                      </div>
                    </div>
                    <div className="text-gray-500 text-right">
                      Last updated: {formatDateToTime12h(new Date())}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PerformanceBar;